import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Landmark, ShieldCheck, ScrollText } from "lucide-react";
import Header from "../components/Header";
import ContactFooter from "../components/ContactFooter";
import { ENTITY_NAME, SUPPORT_EMAIL } from "../data/siteContent";

const pillars = [
  {
    icon: Landmark,
    title: "Trust Planning",
    text: "We design and prepare living trusts that hold, manage, and pass on what you own, without forcing your family through probate.",
  },
  {
    icon: ShieldCheck,
    title: "Estate Protection",
    text: "We structure assets so they hold up against lawsuits, creditors, and the kind of pressure that catches unprepared families off guard.",
  },
  {
    icon: ScrollText,
    title: "Asset Preservation",
    text: "We help you keep control of what you have built for the long term, across generations, on terms you set while you are still able to set them.",
  },
];

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-parchment min-h-screen antialiased">
      <Header light />
      <article className="pt-36 sm:pt-44 pb-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-goldeep text-sm font-medium tracking-wide hover:text-ink transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>

          <div className="flex items-center gap-3 text-[11px] sm:text-xs tracking-[0.3em] uppercase font-semibold text-gold mt-8">
            <span className="h-px w-10 bg-gold/60" />
            About
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-ink tracking-tight leading-[1.1] mt-5">
            Who We <em className="text-goldeep italic">Are.</em>
          </h1>

          <p className="text-ink/70 text-base sm:text-lg leading-relaxed mt-8">
            Iron Gate Consulting is operated by <strong className="text-ink">{ENTITY_NAME}</strong>,
            the entity responsible for the trust planning, estate protection, and asset
            preservation services offered on this site. Every file, every intake, and every
            document delivered through trust.igchq.com is prepared and administered under
            {" "}{ENTITY_NAME}.
          </p>
          <p className="text-ink/70 text-base sm:text-lg leading-relaxed mt-5">
            Our focus is narrow and deliberate: living trusts, asset protection structuring, and
            the coordination of legal review for families, homeowners, veterans, and business
            owners who want real protection instead of paperwork that sits in a drawer. We are
            not a law firm. Legal review is coordinated through licensed attorneys and, where
            applicable, veteran military legal benefits.
          </p>
          <p className="text-ink/70 text-base sm:text-lg leading-relaxed mt-5">
            We do not believe protection should be complicated or reserved for the wealthy. Every
            client goes through the same process: a self-paced portal intake, a personal call
            before any payment, and a completed trust that is reviewed before it is finalized.
          </p>

          <div className="grid sm:grid-cols-3 gap-5 mt-14">
            {pillars.map((pillar) => {
              const Icon = pillar.icon;
              return (
                <div
                  key={pillar.title}
                  className="bg-white border border-ink/10 rounded-2xl p-6 shadow-sm"
                >
                  <div className="w-11 h-11 rounded-full bg-goldeep/10 border border-goldeep/25 flex items-center justify-center text-goldeep">
                    <Icon className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <h3 className="font-display text-lg text-ink mt-5">{pillar.title}</h3>
                  <p className="text-ink/60 text-sm leading-relaxed mt-2">{pillar.text}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-14 bg-ink rounded-2xl p-8 sm:p-10">
            <p className="font-display text-lg sm:text-xl text-cream leading-snug">
              Questions about who operates this site or how your file is handled?
            </p>
            <p className="text-cream/65 text-sm sm:text-base leading-relaxed mt-3">
              Reach us directly at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold underline">
                {SUPPORT_EMAIL}
              </a>{" "}
              or visit our{" "}
              <Link to="/contact" className="text-gold underline">
                Contact page
              </Link>
              .
            </p>
          </div>
        </div>
      </article>
      <ContactFooter />
    </div>
  );
};

export default About;
